import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';
import { subscribeAuth, getUserRole } from '../../services/auth';
import { releaseEscrowToSeller, getSellerPlatformFeeRate } from '../../services/wallet';
import { auth } from '../../lib/firebase';
import { orderDeliveredEmail } from '../../services/email';
import { getUserProfile } from '../../services/auth';

interface Order {
  id: string;
  sellerId: string;
  status: 'payment_held' | 'delivery_started' | 'delivered' | 'completed' | 'cancelled';
  totalAmount: number;
  createdAt: any;
  orderItems?: Array<{
    listingId?: string;
    title?: string;
    price?: number;
    image?: string;
  }>;
  sellerName?: string;
  deliveryAddress?: string;
  buyerConfirmed?: boolean;
  sellerConfirmed?: boolean;
  escrowAmount?: number;
  sellerPayoutAmount?: number;
  platformFeeRate?: number;
}

const BuyerOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [loadingOrderId, setLoadingOrderId] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  useEffect(() => {
    const unsub = subscribeAuth(async (user) => {
      try {
        if (!user) {
          navigate('?login=1', { replace: true });
          setLoading(false);
          return;
        }
        const { role } = await getUserRole(user.uid);
        // Sellers can also view their purchases; no redirect.
        const q = query(
          collection(db, 'orders'),
          where('buyerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const qs = await getDocs(q);
        const ordersData: Order[] = [];
        qs.forEach((d) => ordersData.push({ id: d.id, ...(d.data() as any) } as Order));
        setOrders(ordersData);
      } catch (error) {
        console.error('Error loading orders:', error);
        alert('Failed to load orders. Please try again.');
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [navigate]);

  const filteredOrders = orders.filter(order => {
    const matchesFilter = filter === 'all' ? true : (filter === 'active' ? (order.status !== 'completed' && order.status !== 'cancelled') : order.status === filter);
    const matchesSearch = order.id.toLowerCase().includes(orderSearch.toLowerCase().trim());
    return matchesFilter && (!orderSearch.trim() || matchesSearch);
  });
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const aTime = a.createdAt?.seconds ? a.createdAt.seconds : (a.createdAt?.toDate ? a.createdAt.toDate().getTime() / 1000 : 0);
    const bTime = b.createdAt?.seconds ? b.createdAt.seconds : (b.createdAt?.toDate ? b.createdAt.toDate().getTime() / 1000 : 0);
    return sortOrder === 'newest' ? bTime - aTime : aTime - bTime;
  });

  const handleConfirmDelivery = async (order: Order) => {
    try {
      setLoadingOrderId(order.id);
      const orderRef = doc(db, 'orders', order.id);

      // Verify only the actual buyer can confirm and trigger release
      const preSnap = await getDoc(orderRef);
      const preData = preSnap.exists() ? (preSnap.data() as any) : null;
      const buyerId = preData?.buyerId;
      const currentUid = auth.currentUser?.uid;
      if (!currentUid || buyerId !== currentUid) {
        alert('Only the buyer who placed this order can confirm delivery or release payment.');
        setLoadingOrderId(null);
        return;
      }

      // Mark buyer confirmed and delivered
      await updateDoc(orderRef, {
        buyerConfirmed: true,
        status: 'delivered',
      });

      // Refetch fresh order state from Firestore to avoid stale client flags
      const snap = await getDoc(orderRef);
      let updated = { ...order, buyerConfirmed: true, status: 'delivered' } as Order;
      if (snap.exists()) {
        const data = snap.data() as any;
        const sellerConfirmed = !!data.sellerConfirmed;
        const sellerId = data.sellerId;
        const totalAmount = Number(data.totalAmount || 0);

        if (sellerConfirmed && sellerId && totalAmount > 0) {
          const platformFeeRate = await getSellerPlatformFeeRate(sellerId);
          const sellerPayoutAmount = Math.round(totalAmount * (1 - platformFeeRate));

          // Record that the buyer requested the release
          await updateDoc(orderRef, { releaseRequestedBy: currentUid, platformFeeRate, sellerPayoutAmount, status: 'completed', releasedAt: new Date() });
          await releaseEscrowToSeller(sellerId, totalAmount, order.id, platformFeeRate);

          updated = { ...updated, status: 'completed', platformFeeRate, sellerPayoutAmount } as Order;
          alert('Delivery confirmed by both parties. Funds released to seller.');
        } else {
          alert('Delivery confirmed. Awaiting seller to mark delivered.');
        }

        // Send delivery notifications to both parties
        try {
          const sellerProfile = sellerId ? await getUserProfile(sellerId) : null;
          const sellerEmail = sellerProfile?.email || data?.sellerEmail || undefined;
          const buyerEmail = auth.currentUser?.email || data?.buyerEmail || undefined;
          await orderDeliveredEmail(order.id, sellerEmail, buyerEmail);
        } catch (e) {
          console.warn('Failed to send order-delivered emails', e);
        }
      }

      setOrders(prev => prev.map(o => o.id === order.id ? updated : o));

      // Prompt for review immediately after delivered/completed
      const primaryItemId = (updated.orderItems && updated.orderItems[0]?.listingId) || null;
      if (primaryItemId && (updated.status === 'delivered' || updated.status === 'completed')) {
        navigate(`/product/${primaryItemId}?promptReview=1`);
      }
    } catch (error) {
      console.error('Error confirming delivery:', error);
      alert('Failed to confirm delivery. Please try again.');
    } finally {
      setLoadingOrderId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CampusHeader />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CampusHeader />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-gray-900">Your Orders</h1>
            <p className="text-sm md:text-base text-gray-600 mt-2">Track your purchases and deliveries</p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <input
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              placeholder="Search by Order ID"
              className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Orders</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as any)}
              className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest to Oldest</option>
              <option value="oldest">Oldest to Newest</option>
            </select>
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shopping-bag-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No orders yet</h3>
            <p className="text-gray-600 mb-6">Your purchases will appear here once you place an order</p>
            <button
              onClick={() => navigate('/shop')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse Marketplace
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredOrders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Order #{order.id?.slice(-8) || 'N/A'}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Placed on {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm ${order.status === 'completed' ? 'bg-purple-100 text-purple-800' : order.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                    {order.status === 'completed' ? 'Payment Released' : order.status === 'cancelled' ? 'Cancelled' : 'Active'}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <div className="space-y-4">
                      {(order.orderItems || []).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 md:gap-4 p-2 md:p-3 bg-gray-50 rounded-lg">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-lg overflow-hidden border">
                            {item.image ? (
                              <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <i className="ri-image-line"></i>
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{item.title || 'Item'}</div>
                            <div className="text-sm text-gray-600">₦{Number(item.price || 0).toLocaleString()}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 md:p-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Total Amount</span>
                        <span className="font-semibold text-gray-900">₦{Number(order.totalAmount || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Delivery Address</span>
                        <span className="text-gray-900">{order.deliveryAddress || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="mt-6 flex items-center gap-2 md:gap-3 flex-wrap">
                      <button
                        onClick={() => navigate(`/order/${order.id}`)}
                        className="bg-gray-100 text-gray-700 px-3 md:px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        View Details
                      </button>

                      {(order.status === 'delivery_started' || order.status === 'delivered') && (
                        <button
                          onClick={() => handleConfirmDelivery(order)}
                          className="bg-green-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                          disabled={loadingOrderId === order.id}
                        >
                          {loadingOrderId === order.id ? 'Processing...' : 'Confirm Delivery'}
                        </button>
                      )}

                      {order.status === 'delivered' && order.buyerConfirmed && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                          Delivery Confirmed
                        </span>
                      )}

                      {order.status === 'completed' && (
                        <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                          Payment Released
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BuyerOrdersPage;